import React from "react";

// styles
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";

// components
import { customWidgetTypeHandler } from "./custom-pl-widget-renderer/CustomPLWidgetRenderer";

// types
import { CouponListingPLWidgetTypes } from "@swiggy-private/cart-types";

// utils
import { captureError, GenericError } from "@swiggy-private/core-ui";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";

// dls
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";

interface MxnRendererProps {
    cards: unknown[];
    theme: ReturnType<typeof useTheme>;
}

export async function MxNRenderer(): Promise<React.ComponentType | null> {
    try {
        const module = await import("@swiggy-private/dls-react-mxn/dist");
        const { WidgetControllerContext, PLWidgetController } = module as unknown as {
            WidgetControllerContext: React.Context<{
                sdk: unknown;
                headers: unknown;
            }>;
            PLWidgetController: React.ComponentType<{ cards: unknown[] }>;
        };

        // `AsyncComponent` is typed as `ComponentType<{}>`; accept props loosely and narrow internally.
        const MxnRenderer: React.FC = (props) => {
            const { cards, theme } = props as MxnRendererProps;
            const { sdk, headers } = React.useContext(WidgetControllerContext);

            const contextValue = React.useMemo(
                () => ({
                    sdk,
                    headers,
                    customWidgetTypeHandler,
                    customWidgetTypes: [
                        CouponListingPLWidgetTypes.COUPON_CARD,
                        CouponListingPLWidgetTypes.COUPON_SECTION_COMMS,
                        CouponListingPLWidgetTypes.COUPON_TEXT_BANNER,
                    ],
                }),
                [headers, sdk],
            );

            return (
                <WidgetControllerContext.Provider value={contextValue}>
                    <ThemeProvider theme={theme} design_language={theme.designLanguage} colorMode={theme.colorMode}>
                        <PLWidgetController cards={cards} />
                    </ThemeProvider>
                </WidgetControllerContext.Provider>
            );
        };

        return MxnRenderer;
    } catch (err) {
        captureError(err);
        recordNREvent({
            eventType: NEWRELIC_TABLES.EMPTY_PAGE_RENDERED,
            page: "coupon_listing",
            errorMessage: err instanceof Error ? err.message : "Unknown error",
        });
        return GenericError;
    }
}
